import React from 'react';
import { Search, PanelLeft, Mic, Sun, Moon, ChevronRight, Bot, Upload, Loader2 } from 'lucide-react';
import { Meeting } from '../types';
import { WindowControls } from './WindowControls';
import { t } from '../lib/i18n';

interface TopBarProps {
  onToggleSidebar: () => void;
  onOpenOmnibar: () => void;
  onStartRecord: () => void;
  /** Ses dosyası yükler (seçtirir, yazıya döker, özetler). */
  onUploadAudio: () => void;
  /** Yükleme sürüyorsa aşamanın metni ("Yazıya dökülüyor…"); yoksa null. */
  uploadStatus: string | null;
  /** Toplantıya bot gönderme penceresini açar. */
  onSendBot: () => void;
  isRecording: boolean;
  isDarkMode: boolean;
  onToggleTheme: () => void;
  currentMeeting?: Meeting | null;
  onBackToHome?: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({
  onToggleSidebar,
  onOpenOmnibar,
  onStartRecord,
  onUploadAudio,
  uploadStatus,
  onSendBot,
  isRecording,
  isDarkMode,
  onToggleTheme,
  currentMeeting,
  onBackToHome
}) => {
  return (
    <header className={`app-drag-region h-12 flex items-center justify-between px-3.5 select-none transition-colors duration-150 shrink-0 z-20 ${
      isDarkMode
        ? 'bg-[#111113] text-[#D8D8E0]'
        : 'bg-[#FCFCFD] text-[#1E2024]'
    }`}>
      {/* Left controls */}
      <div className="app-no-drag flex items-center gap-2 min-w-0">
        {/* Toggle Sidebar Icon */}
        <button
          id="btn-toggle-sidebar"
          onClick={onToggleSidebar}
          aria-label={t('Menüyü Daralt/Genişlet', 'Collapse/expand sidebar')}
          title={t('Menüyü Daralt / Genişlet', 'Collapse / expand sidebar')}
          className={`p-2 rounded-md transition-all ${
            isDarkMode 
              ? 'hover:bg-[#1F1F23] text-neutral-400 hover:text-[#F0EEE6]' 
              : 'hover:bg-neutral-200/80 text-neutral-600 hover:text-black'
          }`}
        >
          <PanelLeft className="w-5 h-5" />
        </button>

        {/* Search Trigger */}
        <button
          id="btn-top-search"
          onClick={onOpenOmnibar}
          title={t('Toplantı veya komut ara (⌘K)', 'Search meetings or commands (⌘K)')}
          className={`flex items-center gap-2.5 px-3 py-1.5 rounded-md transition-all ${
            isDarkMode 
              ? 'hover:bg-[#1F1F23] text-neutral-400 hover:text-neutral-200' 
              : 'hover:bg-neutral-200/80 text-neutral-500 hover:text-neutral-900'
          }`}
        >
          <Search className="w-4 h-4" />
          <span className="hidden sm:inline text-[13px]">{t('Ara & Sor', 'Search & Ask')}</span>
          <kbd className={`hidden md:inline text-[11px] font-mono px-2 py-1 rounded border transition-colors ${
            isDarkMode 
              ? 'bg-[#19191C] border-[#434347] text-neutral-400' 
              : 'bg-white border-neutral-200 text-neutral-500 shadow-2xs'
          }`}>⌘K</kbd>
        </button>

        {/* Breadcrumb path if inside a meeting */}
        {currentMeeting && (
          <div className="hidden md:flex items-center gap-2 ml-2 text-[13px] font-medium truncate max-w-[220px] md:max-w-md">
            <span className="text-neutral-500">/</span>
            <button 
              onClick={onBackToHome}
              className={`transition-colors truncate ${
                isDarkMode ? 'text-neutral-400 hover:text-[#F0EEE6]' : 'text-neutral-500 hover:text-black'
              }`}
            >
              {t('Ana Sayfa', 'Home')}
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-neutral-500 shrink-0" />
            <span className={`truncate font-semibold ${isDarkMode ? 'text-[#F0EEE6]' : 'text-neutral-900'}`}>
              {currentMeeting.title}
            </span>
          </div>
        )}
      </div>

      {/* Right controls */}
      <div className="app-no-drag flex items-center gap-1.5 sm:gap-2.5 shrink-0">
        {/* Theme Toggle — telefonda yer yok; tema Ayarlar'dan da değişiyor. */}
        <button
          id="btn-theme-toggle"
          onClick={onToggleTheme}
          title={isDarkMode ? t('Açık Moda Geç', 'Switch to light mode') : t('Karanlık Moda Geç', 'Switch to dark mode')}
          className={`hidden sm:block p-2 rounded-md transition-all ${
            isDarkMode 
              ? 'hover:bg-[#1F1F23] text-neutral-400 hover:text-amber-300' 
              : 'hover:bg-neutral-200/80 text-neutral-600 hover:text-amber-600'
          }`}
        >
          {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>

        {/* Toplantıya bot gönder (Skribby) */}
        <button
          id="btn-send-bot-top"
          onClick={onSendBot}
          title={t('Toplantıya bot gönder', 'Send a bot to a meeting')}
          className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all active:scale-95 ${
            isDarkMode
              ? 'bg-[#1F1F23] hover:bg-[#26262A] text-neutral-200'
              : 'bg-neutral-200/80 hover:bg-neutral-300/80 text-neutral-700'
          }`}
        >
          <Bot className="w-4 h-4" />
          <span className="hidden sm:inline text-[13px] font-semibold">{t('Bot Gönder', 'Send Bot')}</span>
        </button>

        {/* Ses dosyası yükle — sürerken aşaması yanında yazar */}
        <button
          id="btn-upload-audio-top"
          onClick={onUploadAudio}
          disabled={uploadStatus !== null}
          title={uploadStatus ?? t('Ses dosyası yükle', 'Upload an audio file')}
          aria-label={uploadStatus ?? t('Ses dosyası yükle', 'Upload an audio file')}
          className={`flex items-center gap-2 p-2 rounded-md transition-all disabled:cursor-progress ${
            isDarkMode
              ? 'hover:bg-[#1F1F23] text-neutral-300 hover:text-[#F0EEE6]'
              : 'hover:bg-neutral-200/80 text-neutral-600 hover:text-black'
          }`}
        >
          {uploadStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploadStatus && <span className="text-[12.5px] font-medium">{uploadStatus}</span>}
        </button>

        {/* Desktop "+ Record" Button with high visual fidelity */}
        <button
          id="btn-record-top"
          data-tour="record"
          onClick={onStartRecord}
          className={`flex items-center gap-2 px-3.5 py-2 rounded-full tracking-wide transition-all shadow-sm active:scale-95 ${
            isRecording 
              ? 'bg-rose-600 hover:bg-rose-500 text-[#F0EEE6] animate-pulse'
              : 'bg-[#1DA4BA] hover:bg-[#3AC8E0] text-[#F0EEE6] shadow-[#3AC8E0]/20'
          }`}
        >
          <Mic className="w-4 h-4" />
          <span className="text-[13px] font-semibold">{isRecording ? t('Kayıtta', 'Recording') : t('Kayıt Al', 'Record')}</span>
        </button>

        <WindowControls isDarkMode={isDarkMode} />
      </div>
    </header>
  );
};
